import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import {
  MessageSquare, ArrowLeft, Search, FileText, Briefcase,
  Shield, ChevronRight, Hammer, Lock, User
} from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Badge } from '../../components/ui/badge'
import ChatView from '../../components/chat/ChatView'
import DashboardLayout from '../../components/DashboardLayout'
import { useAuth } from '../../context/AuthContext'
import { chatsService } from '../../services/api'
import { messagingPaths, parseMessagingPath } from '../../utils/messagingPaths'
import { getMessagingContextTheme } from '../../components/chat/chat-utils'

const GROUP_FILTERS = [
  { key: 'ALL', label: 'Tous' },
  { key: 'REQUEST', label: 'Demandes' },
  { key: 'JOB', label: 'Emplois' },
  { key: 'ARTISAN', label: 'Artisans' },
]

const formatTime = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diff === 0) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (diff === 1) return 'Hier'
  if (diff < 7) return date.toLocaleDateString('fr-FR', { weekday: 'short' })
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}

const getGroupIcon = (group) => {
  if (group.kind === 'ARTISAN') return <Hammer className="w-4 h-4" />
  if (group.postType === 'JOB') return <Briefcase className="w-4 h-4" />
  if (group.postType === 'REQUEST') return <FileText className="w-4 h-4" />
  return <MessageSquare className="w-4 h-4" />
}

const extractList = (res) => {
  if (Array.isArray(res)) return res
  if (Array.isArray(res?.data)) return res.data
  if (Array.isArray(res?.items)) return res.items
  return []
}

export default function Messaging() {
  const { user, isAdmin, isProView } = useAuth()
  const messagingContext = isProView() ? 'pro' : 'particulier'
  const contextTheme = getMessagingContextTheme(messagingContext)
  const navigate = useNavigate()
  const location = useLocation()
  const { postId, artisanId, conversationId } = parseMessagingPath(location.pathname)
  const view = conversationId ? 'chat' : (postId || artisanId ? 'threads' : 'groups')

  const [groups, setGroups] = useState([])
  const [threads, setThreads] = useState([])
  const [activeChat, setActiveChat] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [groupFilter, setGroupFilter] = useState('ALL')
  const messagesEndRef = useRef(null)
  const pollingRef = useRef(null)
  const activeLoadIdRef = useRef(null)

  const loadGroups = useCallback(async () => {
    try {
      const res = await chatsService.getGroups(
        groupFilter !== 'ALL' ? { type: groupFilter } : {}
      )
      setGroups(extractList(res))
    } catch (error) {
      console.error('[Messaging] groups error:', error)
    }
  }, [groupFilter])

  const loadThreads = useCallback(async (opts = {}) => {
    const { postId: pId, artisanId: aId } = opts
    const resolvedPostId = pId ?? postId
    const resolvedArtisanId = aId ?? artisanId

    if (!resolvedPostId && !resolvedArtisanId) return []

    try {
      const params = resolvedPostId
        ? { postId: resolvedPostId, limit: 50 }
        : { peerUserId: resolvedArtisanId, limit: 50 }
      const res = await chatsService.getChats(params)
      const list = extractList(res)
      setThreads(list)
      return list
    } catch (error) {
      console.error('[Messaging] threads error:', error)
      toast.error('Erreur lors du chargement des discussions')
      return []
    }
  }, [postId, artisanId])

  const loadMessages = useCallback(async (chatId) => {
    try {
      const res = await chatsService.getMessages(chatId, { limit: 100 })
      const list = extractList(res)
      setMessages(list)
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    } catch (error) {
      console.error('[Messaging] messages error:', error)
    }
  }, [])

  const loadActiveChat = useCallback(async (chatId) => {
    activeLoadIdRef.current = chatId

    try {
      const res = await chatsService.getChatById(chatId)
      if (activeLoadIdRef.current !== chatId) return

      const chat = res?.data ?? res
      if (!chat?.id) {
        throw new Error('Conversation introuvable')
      }

      setActiveChat(chat)

      const chatPostId = chat.postId ?? chat.post?.id
      const resolvedPostId = postId ?? chatPostId
      const peer = chat.participants?.find(
        (p) => p.userId !== user?.id && p.roleInChat !== 'ADMIN'
      )
      const resolvedArtisanId = artisanId ?? (chatPostId ? null : peer?.userId)

      if (resolvedPostId) {
        await loadThreads({ postId: resolvedPostId })
      } else if (resolvedArtisanId) {
        await loadThreads({ artisanId: resolvedArtisanId })
      }

      if (activeLoadIdRef.current !== chatId) return

      await loadMessages(chatId)

      try {
        await chatsService.markAsRead(chatId)
      } catch (readErr) {
        console.warn('[Messaging] markAsRead:', readErr)
      }

      if (activeLoadIdRef.current !== chatId) return

      if (/\/messagerie\/chat\/[^/]+$/.test(location.pathname)) {
        const canonical = messagingPaths.chatFromRecord(chat, user?.id)
        if (canonical !== location.pathname) {
          navigate(canonical, { replace: true })
        }
      }
    } catch (error) {
      if (activeLoadIdRef.current !== chatId) return

      console.error('[Messaging] chat error:', error)
      const isNotFound = error?.status === 404
      toast.error(isNotFound ? 'Conversation introuvable' : error?.message ?? 'Erreur lors du chargement')

      if (postId) {
        navigate(messagingPaths.postThreads(postId))
      } else if (artisanId) {
        navigate(messagingPaths.artisanThreads(artisanId))
      } else {
        navigate(messagingPaths.root())
      }
    }
  }, [loadMessages, loadThreads, navigate, postId, artisanId, user?.id, location.pathname])

  useEffect(() => {
    const stateChatId = location.state?.chatId
    if (stateChatId && view === 'groups') {
      navigate(messagingPaths.chat({ chatId: stateChatId }), { replace: true, state: {} })
    }
  }, [location.state, view, navigate])

  useEffect(() => {
    if (view === 'groups') {
      setLoading(true)
      loadGroups().finally(() => setLoading(false))
    } else {
      loadGroups()
    }
  }, [view, loadGroups])

  useEffect(() => {
    if (view !== 'threads') return
    setLoading(true)
    loadThreads().finally(() => setLoading(false))
  }, [view, postId, artisanId, loadThreads])

  useEffect(() => {
    if (view !== 'chat' || !conversationId) {
      if (view !== 'chat') {
        setActiveChat(null)
        setMessages([])
      }
      return
    }
    setLoading(true)
    loadActiveChat(conversationId).finally(() => setLoading(false))
  }, [view, conversationId, loadActiveChat])

  useEffect(() => {
    if (conversationId && view === 'chat') {
      pollingRef.current = setInterval(() => {
        loadMessages(conversationId)
        loadGroups()
        if (postId || artisanId) loadThreads()
      }, 5000)
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [conversationId, view, postId, artisanId, loadMessages, loadGroups, loadThreads])

  const refreshChatData = useCallback(async () => {
    if (!conversationId) return
    await loadMessages(conversationId)
    loadGroups()
    if (postId || artisanId) loadThreads()
    try {
      const res = await chatsService.getChatById(conversationId)
      const chat = res?.data ?? res
      if (chat?.id) setActiveChat(chat)
    } catch (error) {
      console.warn('[Messaging] refresh chat:', error)
    }
  }, [conversationId, loadMessages, loadGroups, loadThreads, postId, artisanId])

  const handleMessageSent = useCallback((msg) => {
    setMessages((prev) => [...prev, msg])
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }, [])

  const getOtherParticipant = (chat) => {
    if (!chat?.participants) return null
    return chat.participants.find(
      (p) => p.userId !== user?.id && p.roleInChat !== 'ADMIN'
    )
  }

  const activeGroup = useMemo(() => {
    if (postId) return groups.find((g) => g.postId === postId)
    if (artisanId) return groups.find((g) => g.peerUserId === artisanId)
    const activePostId = activeChat?.postId ?? activeChat?.post?.id
    if (activePostId) return groups.find((g) => g.postId === activePostId)
    const peer = getOtherParticipant(activeChat)
    if (peer) return groups.find((g) => g.peerUserId === peer.userId)
    return null
  }, [groups, postId, artisanId, activeChat, user?.id])

  const filteredGroups = groups.filter((g) =>
    g.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.locationCity?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const threadsBackPath = messagingPaths.root()
  const chatBackPath = postId
    ? messagingPaths.postThreads(postId)
    : artisanId
    ? messagingPaths.artisanThreads(artisanId)
    : activeGroup
    ? messagingPaths.group(activeGroup)
    : messagingPaths.root()

  const threadChatPath = (chat) =>
    messagingPaths.chat({
      chatId: chat.id,
      postId: postId ?? activeChat?.postId ?? activeChat?.post?.id,
      artisanId,
    })

  const renderThreadRow = (chat, compact = false) => {
    const other = getOtherParticipant(chat)
    const isActive = chat.id === conversationId
    const preview = chat.messages?.[0]?.text ?? 'Aucun message'

    return (
      <Link
        key={chat.id}
        to={threadChatPath(chat)}
        className={`flex items-center gap-3 p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
          isActive ? 'bg-blue-50 border-l-4 border-l-[hsl(var(--primary))]' : ''
        }`}
      >
        <div className="relative shrink-0">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold ${
            other?.user?.proStatus === 'APPROVED'
              ? 'bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))]'
              : 'bg-slate-400'
          }`}>
            {other?.user?.photoUrl ? (
              <img src={other.user.photoUrl} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              other?.user?.name?.charAt(0) || 'U'
            )}
          </div>
          {other?.user?.proStatus === 'APPROVED' && (
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center">
              <Shield className="w-3 h-3 text-[hsl(var(--primary))]" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 truncate">
              {other?.user?.name ?? 'Conversation'}
            </span>
            {chat.unreadCount > 0 && (
              <span className="w-5 h-5 bg-[hsl(var(--primary))] text-white text-xs rounded-full flex items-center justify-center shrink-0">
                {chat.unreadCount}
              </span>
            )}
          </div>
          {!compact && chat.application?.status && (
            <Badge variant="outline" className="text-xs mt-1">
              {chat.application.status}
            </Badge>
          )}
          <p className="text-sm text-slate-500 truncate mt-0.5">{preview}</p>
        </div>
        <div className="text-xs text-slate-400 whitespace-nowrap">
          {chat.messages?.[0] ? formatTime(chat.messages[0].createdAt) : ''}
        </div>
      </Link>
    )
  }

  const renderGroupRow = (group) => {
    const path = messagingPaths.group(group)
    const preview = group.lastMessage?.text ?? 'Aucun message'

    return (
      <Link
        key={group.id}
        to={path}
        className="flex items-center gap-3 p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors group"
      >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
          contextTheme.isPro
            ? 'bg-amber-50 text-amber-700'
            : 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]'
        }`}>
          {getGroupIcon(group)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 truncate group-hover:text-[hsl(var(--primary))] transition-colors">{group.title}</span>
            {group.unreadCount > 0 && (
              <span className="w-5 h-5 bg-[hsl(var(--primary))] text-white text-xs rounded-full flex items-center justify-center shrink-0">
                {group.unreadCount}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">
            {group.subtitle}
            {group.locationCity ? ` · ${group.locationCity}` : ''}
          </p>
          <p className="text-sm text-slate-400 truncate mt-0.5">{preview}</p>
          {group.chatCount > 1 && (
            <p className="text-xs text-[hsl(var(--primary))] mt-1">
              {group.chatCount} discussion{group.chatCount > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-400">
            {group.lastMessage ? formatTime(group.lastMessage.createdAt) : ''}
          </span>
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </div>
      </Link>
    )
  }

  const showThreadsSidebar = view === 'chat' && threads.length > 0

  return (
    <DashboardLayout flush>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {view !== 'groups' && (
        <header className="md:hidden bg-white border-b border-slate-200 h-14 flex items-center px-4 gap-3 shrink-0">
          {view === 'chat' ? (
            <>
              <button
                type="button"
                onClick={() => navigate(chatBackPath)}
                className="flex items-center gap-1 text-slate-600 hover:text-slate-900 shrink-0"
                aria-label="Retour aux discussions"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Retour</span>
              </button>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">
                  {getOtherParticipant(activeChat)?.user?.name ?? activeGroup?.title ?? 'Conversation'}
                </p>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5" />
                  {contextTheme.label}
                </p>
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate(threadsBackPath)}
                className="flex items-center gap-1 text-slate-600 hover:text-slate-900 shrink-0"
                aria-label="Retour à la messagerie"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Retour</span>
              </button>
              <p className="font-semibold truncate">{activeGroup?.title ?? 'Discussions'}</p>
            </>
          )}
        </header>
      )}

      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Panneau groupes */}
        <aside
          className={`${
            view === 'groups' ? 'flex' : 'hidden'
          } md:flex w-full md:w-80 lg:w-96 bg-white border-r border-slate-200 flex-col ${
            conversationId ? 'md:hidden' : ''
          }`}
        >
          <div className="p-4 border-b border-slate-200 bg-slate-50/60">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h1 className="text-xl font-bold text-slate-900">Messagerie</h1>
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${contextTheme.badgeClass}`}>
                {contextTheme.isPro ? <Briefcase className="w-3 h-3" /> : <User className="w-3 h-3" />}
                {contextTheme.shortLabel}
              </span>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                className="pl-10 border-2 border-slate-200 rounded-xl bg-white focus-visible:ring-slate-200"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {GROUP_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setGroupFilter(f.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    groupFilter === f.key
                      ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && view === 'groups' ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse h-16 bg-slate-100 rounded-xl" />
                ))}
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucune conversation</p>
                <a href="https://clicketpro.fr/artisans">
                  <Button className="btn-clickpro-primary mt-4" size="sm">
                    Trouver un artisan
                  </Button>
                </a>
              </div>
            ) : (
              filteredGroups.map(renderGroupRow)
            )}
          </div>
        </aside>

        {/* Panneau discussions (niveau 2 ou sidebar chat) */}
        <aside
          className={`${
            view === 'threads' ? 'flex' : showThreadsSidebar ? 'hidden md:flex' : 'hidden'
          } w-full md:w-80 bg-white border-r border-slate-200 flex-col`}
        >
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => navigate(threadsBackPath)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors shrink-0"
                aria-label="Retour à la messagerie"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Retour</span>
              </button>
              <h2 className="font-semibold text-slate-900 truncate min-w-0">
                {activeGroup?.title ?? 'Discussions'}
              </h2>
            </div>
            {activeGroup && (
              <p className="text-sm text-slate-500">
                {threads.length} discussion{threads.length > 1 ? 's' : ''}
                {activeGroup.locationCity ? ` · ${activeGroup.locationCity}` : ''}
              </p>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && view === 'threads' ? (
              <div className="p-4 space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse h-14 bg-slate-100 rounded-lg" />
                ))}
              </div>
            ) : threads.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                Aucune discussion pour ce sujet
              </div>
            ) : (
              threads.map((t) => renderThreadRow(t, showThreadsSidebar))
            )}
          </div>
        </aside>

        {/* Zone de chat */}
        <main
          className={`flex-1 flex flex-col min-h-0 ${
            view !== 'chat' ? 'hidden md:flex' : 'flex'
          } ${contextTheme.messageAreaClass}`}
        >
          {view === 'chat' && activeChat ? (
            <>
              <div className={`hidden md:flex bg-white border-b border-slate-200 h-16 items-center px-4 gap-3 shrink-0 border-t-4 ${contextTheme.panelAccentClass}`}>
                <button
                  type="button"
                  onClick={() => navigate(chatBackPath)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-slate-200 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 transition-colors shrink-0"
                  aria-label="Retour aux discussions"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">Retour</span>
                </button>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${
                  contextTheme.isPro
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                    : 'bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))]'
                }`}>
                  {getOtherParticipant(activeChat)?.user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-slate-900 truncate">
                      {getOtherParticipant(activeChat)?.user?.name ?? 'Conversation'}
                    </h2>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide shrink-0 ${contextTheme.badgeClass}`}>
                      <Lock className="w-2.5 h-2.5" />
                      {contextTheme.shortLabel}
                    </span>
                  </div>
                  {activeGroup && (
                    <p className="text-sm text-slate-500 truncate">{activeGroup.title}</p>
                  )}
                </div>
                {getOtherParticipant(activeChat)?.user?.proStatus === 'APPROVED' && (
                  <Badge className="badge-clickpro-verified shrink-0">
                    <Shield className="w-3 h-3 mr-1" />
                    Pro vérifié
                  </Badge>
                )}
              </div>

              <ChatView
                chat={activeChat}
                messages={messages}
                currentUserId={user?.id}
                isPlatformAdmin={isAdmin()}
                onMessageSent={handleMessageSent}
                onRefresh={refreshChatData}
                messagesEndRef={messagesEndRef}
                messagingContext={messagingContext}
              />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center px-6 max-w-md">
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 ${
                  contextTheme.isPro ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
                }`}>
                  <MessageSquare className={`w-10 h-10 ${contextTheme.isPro ? 'text-amber-600' : 'text-slate-400'}`} />
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold uppercase tracking-wide mb-3 ${contextTheme.badgeClass}`}>
                  <Lock className="w-3 h-3" />
                  {contextTheme.label}
                </span>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Vos messages</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {view === 'groups'
                    ? 'Sélectionnez une demande, offre ou contact artisan pour voir les discussions'
                    : 'Sélectionnez une discussion pour commencer'}
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
      </div>
    </DashboardLayout>
  )
}
